import { useParams, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMedicalRecord, useDoctorProfile, useSignMedicalRecord, useMedicalRecordTimeline, useDiagnosesByMedicalRecord, usePrescriptionsByMedicalRecord, useExamRequestsByMedicalRecord, useClinicalEvolutionsByMedicalRecord, useCertificatesByMedicalRecord, useCloseMedicalRecord, usePatientAllergies } from '@/hooks';
import { useAuth } from '@/contexts/AuthContext';
import { medicalRecordsApi } from '@/api';
import { useMemedPrescription, extractMemedPrescriptionId } from '@/hooks/useMemedPrescription';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { DiagnosisSelector } from '@/components/medical-records/DiagnosisSelector';
import { PrescriptionEditor } from '@/components/medical-records/PrescriptionEditor';
import { ExamRequestEditor } from '@/components/medical-records/ExamRequestEditor';
import { ClinicalEvolutionEditor } from '@/components/medical-records/ClinicalEvolutionEditor';
import { MedicalTimeline } from '@/components/medical-records/MedicalTimeline';
import { CertificatePreview } from '@/components/medical-records/CertificatePreview';
import { MedicalAttachmentUploader } from '@/components/medical-records/MedicalAttachmentUploader';

export function MedicalRecordPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'info' | 'diagnoses' | 'prescriptions' | 'exams' | 'evolutions' | 'certificates' | 'attachments' | 'timeline'>('info');

  const { data: medicalRecord, isLoading, isError } = useMedicalRecord(id || '');
  const { data: doctorProfile } = useDoctorProfile(medicalRecord?.doctorId || '');
  const { data: diagnoses, isLoading: diagnosesLoading } = useDiagnosesByMedicalRecord(id || '');
  const { data: prescriptions, isLoading: prescriptionsLoading } = usePrescriptionsByMedicalRecord(id || '');
  const { data: examRequests, isLoading: examsLoading } = useExamRequestsByMedicalRecord(id || '');
  const { data: evolutions, isLoading: evolutionsLoading } = useClinicalEvolutionsByMedicalRecord(id || '');
  const { data: certificates, isLoading: certificatesLoading } = useCertificatesByMedicalRecord(id || '');
  const { data: timelineEvents, isLoading: timelineLoading } = useMedicalRecordTimeline(id || '');
  const { data: patientAllergies } = usePatientAllergies(medicalRecord?.patientId);

  const { onMemedEvent } = useMemedPrescription();

  const refreshPrescriptions = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['medical-records'] });
  }, [queryClient]);

  const handleMemedPrescriptionPrinted = useCallback(
    (memedPrescriptionId: string) => {
      const target = prescriptions?.find((prescription) => !prescription.memedPrescriptionId);
      if (target) {
        medicalRecordsApi
          .linkMemedPrescription(target.id, memedPrescriptionId)
          .then(refreshPrescriptions)
          .catch((err) => {
            console.error('Falha ao vincular prescrição à MeMed:', err);
          });
        return;
      }

      // Se não houver prescrição pendente (médico prescreveu diretamente pela MeMed sem rascunho no Medikei),
      // cria nova prescrição no prontuário vinculando o memedPrescriptionId.
      if (!medicalRecord?.id || !medicalRecord?.patientId) {
        return;
      }

      medicalRecordsApi
        .createPrescription({
          medicalRecordId: medicalRecord.id,
          patientId: medicalRecord.patientId,
          doctorId: medicalRecord.doctorId || user?.id || '',
          medications: [
            {
              name: 'Prescrição emitida via MeMed',
              presentation: '',
              concentration: '',
              dose: 'Conforme receituário MeMed',
              frequency: '',
              duration: '',
              quantity: 1,
              observations: `Prescrição emitida diretamente via MeMed (ID: ${memedPrescriptionId})`,
            },
          ],
          type: 'CONTINUOUS',
          instructions: `Emitida via integração MeMed (ID: ${memedPrescriptionId})`,
          memedPrescriptionId,
        })
        .then(async (createdRx) => {
          if (createdRx?.id && (!createdRx.memedPrescriptionId || createdRx.memedPrescriptionId !== memedPrescriptionId)) {
            try {
              await medicalRecordsApi.linkMemedPrescription(createdRx.id, memedPrescriptionId);
            } catch (linkErr) {
              console.error('Falha ao vincular memedPrescriptionId na nova prescrição:', linkErr);
            }
          }
          refreshPrescriptions();
        })
        .catch((err) => {
          console.error('Falha ao registrar prescrição MeMed no prontuário:', err);
        });
    },
    [prescriptions, refreshPrescriptions, medicalRecord, user]
  );

  const handleMemedPrescriptionDeleted = useCallback(
    (memedPrescriptionId: string) => {
      const target = prescriptions?.find(
        (prescription) => prescription.memedPrescriptionId === memedPrescriptionId
      );
      if (!target) {
        return;
      }

      medicalRecordsApi
        .markMemedPrescriptionExcluded(target.id)
        .then(refreshPrescriptions)
        .catch((err) => {
          console.error('Falha ao marcar prescrição como excluída na MeMed:', err);
        });
    },
    [prescriptions, refreshPrescriptions]
  );

  useEffect(() => {
    const unsubscribePrinted = onMemedEvent('prescricaoImpressa', (prescriptionData) => {
      const memedId = extractMemedPrescriptionId(prescriptionData);
      if (memedId) {
        handleMemedPrescriptionPrinted(memedId);
      }
    });
    const unsubscribeDeleted = onMemedEvent(
      'prescricaoExcluida',
      (prescriptionId) => handleMemedPrescriptionDeleted(String(prescriptionId))
    );

    return () => {
      unsubscribePrinted();
      unsubscribeDeleted();
    };
  }, [onMemedEvent, handleMemedPrescriptionPrinted, handleMemedPrescriptionDeleted]);

  const signRecord = useSignMedicalRecord();
  const closeRecord = useCloseMedicalRecord();

  const isDoctor = user?.role === 'DOCTOR';
  const canEdit = medicalRecord?.status !== 'SIGNED' && medicalRecord?.status !== 'COMPLETED' && isDoctor;

  if (isLoading) {
    return (
      <div className="container max-w-4xl py-6">
        <div className="animate-pulse space-y-4">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !medicalRecord) {
    return (
      <div className="container max-w-4xl py-6">
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">Prontuário não encontrado</h2>
          <p className="text-gray-500 mt-2">O prontuário que você está procurando não existe ou foi removido.</p>
          <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">Voltar</Button>
        </div>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-800', IN_PROGRESS: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-green-100 text-green-800', SIGNED: 'bg-purple-100 text-purple-800',
    CANCELLED: 'bg-red-100 text-red-800',
  };
  const statusLabels: Record<string, string> = {
    DRAFT: 'Rascunho', IN_PROGRESS: 'Em Andamento', COMPLETED: 'Completo',
    SIGNED: 'Assinado', CANCELLED: 'Cancelado',
  };

  const allTabs = [
    { key: 'info' as const, label: 'Informações' },
    { key: 'diagnoses' as const, label: `Diagnósticos (${diagnoses?.length || 0})` },
    { key: 'prescriptions' as const, label: `Prescrições (${prescriptions?.length || 0})` },
    { key: 'exams' as const, label: `Exames (${examRequests?.length || 0})` },
    { key: 'evolutions' as const, label: `Evoluções (${evolutions?.length || 0})` },
    { key: 'certificates' as const, label: `Atestados (${certificates?.length || 0})` },
    { key: 'attachments' as const, label: 'Anexos' },
    { key: 'timeline' as const, label: 'Timeline' },
  ];

  const patientTabs = allTabs.filter(tab =>
    ['info', 'prescriptions', 'certificates', 'exams'].includes(tab.key)
  );

  const tabs = isDoctor ? allTabs : patientTabs;

  const handleSign = async () => {
    if (!user?.id) return;
    try {
      await signRecord.mutateAsync({ recordId: medicalRecord.id, signedBy: user.id, signature: `STUB_SIG_${Date.now()}` });
    } catch (e) { console.error(e); }
  };

  const handleClose = async () => {
    if (!user?.id) return;
    try {
      await closeRecord.mutateAsync({ recordId: medicalRecord.id, closedBy: user.id });
    } catch (e) { console.error(e); }
  };

  return (
    <div className="container max-w-4xl py-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Voltar
      </button>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Prontuário Médico</h1>
              <p className="text-sm text-gray-500 mt-1">ID: {medicalRecord.id}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[medicalRecord.status] || 'bg-gray-100 text-gray-800'}`}>
                {statusLabels[medicalRecord.status] || medicalRecord.status}
              </span>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-gray-200">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Paciente</h3>
              <p className="mt-1 text-lg font-medium text-gray-900">{medicalRecord.patientId}</p>
              <p className="text-xs text-gray-400 mt-1">Appointment: {medicalRecord.appointmentId}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Médico</h3>
              <p className="mt-1 text-lg font-medium text-gray-900">{doctorProfile?.displayName || medicalRecord.doctorId}</p>
              <p className="text-xs text-gray-400 mt-1">Versão: {medicalRecord.version}</p>
            </div>
          </div>
        </div>

        <div className="px-6">
          <div className="flex gap-1 -mb-px overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'info' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Criado em</h3>
                  <p className="mt-1 text-gray-900">{new Date(medicalRecord.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Última atualização</h3>
                  <p className="mt-1 text-gray-900">{new Date(medicalRecord.updatedAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
              {medicalRecord.signedAt && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-purple-900">Assinado em</h3>
                  <p className="mt-1 text-purple-700">{new Date(medicalRecord.signedAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  <p className="text-sm text-purple-600 mt-1">Por: {medicalRecord.signedBy}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'diagnoses' && (
            <div className="space-y-4">
              {diagnosesLoading ? (
                <div className="animate-pulse space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
              ) : (
                <>
                  <DiagnosisSelector medicalRecordId={medicalRecord.id} existingDiagnoses={diagnoses} />
                </>
              )}
            </div>
          )}

          {activeTab === 'prescriptions' && (
            <div className="space-y-4">
              {prescriptionsLoading ? (
                <div className="animate-pulse space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
              ) : (
                <PrescriptionEditor medicalRecordId={medicalRecord.id} patientId={medicalRecord.patientId} doctorId={medicalRecord.doctorId} allergies={patientAllergies} existingPrescriptions={prescriptions} onPrescriptionPrinted={handleMemedPrescriptionPrinted} disabled={!canEdit} />
              )}
            </div>
          )}

          {activeTab === 'exams' && (
            <div className="space-y-4">
              {examsLoading ? (
                <div className="animate-pulse space-y-2"><Skeleton className="h-12 w-full" /></div>
              ) : (
                <ExamRequestEditor medicalRecordId={medicalRecord.id} patientId={medicalRecord.patientId} doctorId={medicalRecord.doctorId} appointmentId={medicalRecord.appointmentId} existingExamRequests={examRequests} />
              )}
            </div>
          )}

          {activeTab === 'evolutions' && (
            <div className="space-y-4">
              {evolutionsLoading ? (
                <div className="animate-pulse space-y-2"><Skeleton className="h-12 w-full" /></div>
              ) : (
                <ClinicalEvolutionEditor medicalRecordId={medicalRecord.id} appointmentId={medicalRecord.appointmentId} doctorId={medicalRecord.doctorId} existingEvolutions={evolutions} />
              )}
            </div>
          )}

          {activeTab === 'certificates' && (
            <div className="space-y-4">
              {certificatesLoading ? (
                <div className="animate-pulse space-y-2"><Skeleton className="h-20 w-full" /></div>
              ) : certificates && certificates.length > 0 ? (
                <div className="space-y-4">
                  {certificates.map(cert => <CertificatePreview key={cert.id} certificate={cert} />)}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
                  <p className="text-sm">Nenhum certificado emitido.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'attachments' && (
            <MedicalAttachmentUploader medicalRecordId={medicalRecord.id} />
          )}

          {activeTab === 'timeline' && (
            <div className="space-y-4">
              {timelineLoading ? (
                <div className="animate-pulse space-y-4"><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></div>
              ) : (
                <MedicalTimeline medicalRecord={medicalRecord} events={timelineEvents?.map((e, idx) => ({
                  id: `evt-${idx}`,
                  type: (e.type === 'created' || e.type === 'signed' || e.type === 'record_open' || e.type === 'record_sign' || e.type === 'record_close' ? 'certificate' : e.type) as 'appointment' | 'diagnosis' | 'prescription' | 'exam' | 'certificate' | 'evolution' | 'attachment',
                  icon: (e.type === 'created' || e.type === 'signed' || e.type === 'record_open' || e.type === 'record_sign' || e.type === 'record_close' ? 'certificate' : e.type === 'prescription' ? 'prescription' : e.type === 'diagnosis' ? 'diagnosis' : e.type === 'exam_request' ? 'exam' : e.type === 'evolution' ? 'evolution' : e.type === 'certificate' ? 'certificate' : 'appointment') as 'appointment' | 'diagnosis' | 'prescription' | 'exam' | 'certificate' | 'evolution' | 'attachment',
                  title: e.description.split(':')[0] || e.description,
                  description: e.description,
                  date: e.date,
                }))} />
              )}
            </div>
          )}
        </div>

        {canEdit && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
            <Button
              variant="outline"
              onClick={() =>
                navigate(
                  user?.role === 'DOCTOR'
                    ? `/medico/prontuarios/${medicalRecord.id}/editar`
                    : `/medical-records/${medicalRecord.id}/edit`
                )
              }
            >
              Editar Prontuário
            </Button>
            {medicalRecord.status === 'IN_PROGRESS' && (
              <Button onClick={handleSign} disabled={signRecord.isPending}>
                {signRecord.isPending ? 'Assinando...' : 'Assinar Prontuário'}
              </Button>
            )}
            {(medicalRecord.status === 'DRAFT' || medicalRecord.status === 'IN_PROGRESS') && (
              <Button variant="outline" onClick={handleClose} disabled={closeRecord.isPending}>
                {closeRecord.isPending ? 'Fechando...' : 'Fechar Prontuário'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
